import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../app/components/ui/card";
import { Input } from "../../../app/components/ui/input";
import { Label } from "../../../app/components/ui/label";
import { Button } from "../../../app/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../app/components/ui/tabs";
import { Separator } from "../../../app/components/ui/separator";
import { Badge } from "../../../app/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../app/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../../app/components/ui/accordion";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { useAuth } from "../../../providers/AuthProvider";
import { ProductSearchDropdown, type ProductSearchOption } from "../../inventory/components/ProductSearchDropdown";
import { useProducts } from "../../products/hooks/useProducts";

type PromotionRow = {
  id: string;
  name: string;
  type: string;
  code: string | null;
  status: string;
  start_at: string | null;
  end_at: string | null;
  priority: number;
  rules: Array<{ id: string; rule_type: string; operator: string; value: any }>;
  benefits: Array<{ id: string; benefit_type: string; value: any }>;
  product_ids: string[];
  usage: null | { usage_limit: number | null; used_count: number };
};

export function PromotionsPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<PromotionRow[]>([]);
  const { products } = useProducts();

  const productNameById = useMemo(() => {
    const m: Record<string, { name: string; sku: string | null }> = {};
    for (const p of products) {
      m[String(p.id)] = { name: p.name, sku: p.sku ?? null };
    }
    return m;
  }, [products]);

  const [tab, setTab] = useState<"discount" | "combo" | "bxgy" | "promocode">("discount");

  const [name, setName] = useState("");
  const [status, setStatus] = useState<"active" | "inactive">("active");
  const [priority, setPriority] = useState(0);
  const [startAt, setStartAt] = useState<string>("");
  const [endAt, setEndAt] = useState<string>("");
  const [minCartValue, setMinCartValue] = useState<number | "">("");

  // discount/promocode
  const [discountMode, setDiscountMode] = useState<"flat" | "percent">("percent");
  const [discountAmount, setDiscountAmount] = useState<number | "">(10);
  const [promoCode, setPromoCode] = useState("");

  // combo
  const [comboProducts, setComboProducts] = useState<ProductSearchOption[]>([]);
  const [comboBundlePrice, setComboBundlePrice] = useState<number | "">("");

  // bxgy
  const [buyProduct, setBuyProduct] = useState<ProductSearchOption | null>(null);
  const [buyQty, setBuyQty] = useState<number | "">(2);
  const [getProduct, setGetProduct] = useState<ProductSearchOption | null>(null);
  const [getQty, setGetQty] = useState<number | "">(1);

  const appliedType = useMemo(() => {
    if (tab === "discount") return "discount";
    if (tab === "combo") return "combo";
    if (tab === "bxgy") return "bxgy";
    return "promocode";
  }, [tab]);

  async function load() {
    if (!token) return;
    setLoading(true);
    try {
      const res = await api.promotions.list({ token });
      setRows(
        res.data.map((p) => ({
          id: p.id,
          name: p.name,
          type: p.type,
          code: p.code,
          status: p.status,
          start_at: p.start_at,
          end_at: p.end_at,
          priority: p.priority,
          rules: p.rules ?? [],
          benefits: p.benefits ?? [],
          product_ids: p.product_ids ?? [],
          usage: p.usage ?? null,
        })),
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load promotions");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function onCreate() {
    if (!token) return;
    const n = name.trim();
    if (!n) {
      toast.error("Enter offer name");
      return;
    }

    const rules: Array<{ rule_type: string; operator: string; value: unknown }> = [];
    if (minCartValue !== "" && Number.isFinite(minCartValue) && minCartValue > 0) {
      rules.push({ rule_type: "cart_value", operator: "gte", value: { amount: String(minCartValue.toFixed(2)) } });
    }

    const common = {
      name: n,
      type: appliedType,
      status,
      priority,
      start_at: startAt ? new Date(startAt).toISOString() : null,
      end_at: endAt ? new Date(endAt).toISOString() : null,
      rules: rules.length ? rules : undefined,
    };

    let payload: any = common;

    if (tab === "discount") {
      if (discountAmount === "" || !Number.isFinite(discountAmount) || discountAmount <= 0) {
        toast.error("Enter discount value");
        return;
      }
      payload = {
        ...common,
        benefits: [
          {
            benefit_type: "discount",
            value:
              discountMode === "percent"
                ? { scope: "cart", mode: "percent", percent: String(discountAmount) }
                : { scope: "cart", mode: "flat", amount: String(Number(discountAmount).toFixed(2)) },
          },
        ],
      };
    }

    if (tab === "combo") {
      const ids = comboProducts.map((p) => p.id);
      if (ids.length < 2) {
        toast.error("Select at least 2 products for combo");
        return;
      }
      if (comboBundlePrice === "" || !Number.isFinite(comboBundlePrice) || comboBundlePrice <= 0) {
        toast.error("Enter bundle price");
        return;
      }
      payload = {
        ...common,
        product_ids: ids,
        benefits: [
          {
            benefit_type: "fixed_price",
            value: { product_ids: ids, bundle_price: String(Number(comboBundlePrice).toFixed(2)) },
          },
        ],
      };
    }

    if (tab === "bxgy") {
      if (!buyProduct || !getProduct || buyQty === "" || getQty === "") {
        toast.error("Fill BXGY fields");
        return;
      }
      payload = {
        ...common,
        benefits: [
          {
            benefit_type: "free_item",
            value: {
              buy_product_id: Number(buyProduct.id),
              buy_qty: String(Number(buyQty).toFixed(3)),
              get_product_id: Number(getProduct.id),
              get_qty: String(Number(getQty).toFixed(3)),
            },
          },
        ],
      };
    }

    if (tab === "promocode") {
      const code = promoCode.trim();
      if (!code) {
        toast.error("Enter promo code");
        return;
      }
      if (discountAmount === "" || !Number.isFinite(discountAmount) || discountAmount <= 0) {
        toast.error("Enter discount value");
        return;
      }
      payload = {
        ...common,
        code,
        benefits: [
          {
            benefit_type: "discount",
            value:
              discountMode === "percent"
                ? { scope: "cart", mode: "percent", percent: String(discountAmount) }
                : { scope: "cart", mode: "flat", amount: String(Number(discountAmount).toFixed(2)) },
          },
        ],
      };
    }

    setLoading(true);
    try {
      await api.promotions.create({ token, payload });
      toast.success("Promotion created");
      setName("");
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create promotion");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-semibold text-foreground mb-1">Promotions & Offers</h1>
        <p className="text-muted-foreground">Create rule-based offers for POS checkout</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>Create offer</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
              <TabsList className="w-full">
                <TabsTrigger value="discount">Discount</TabsTrigger>
                <TabsTrigger value="combo">Combo</TabsTrigger>
                <TabsTrigger value="bxgy">Buy X Get Y</TabsTrigger>
                <TabsTrigger value="promocode">Promo Code</TabsTrigger>
              </TabsList>

              <div className="space-y-3 mt-4">
                <div className="space-y-1.5">
                  <Label>Offer name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Summer Sale 10%" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Status</Label>
                    <Input value={status} onChange={(e) => setStatus(e.target.value as any)} placeholder="active/inactive" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Priority</Label>
                    <Input
                      type="number"
                      value={priority}
                      onChange={(e) => setPriority(Number(e.target.value))}
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Start (optional)</Label>
                    <Input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>End (optional)</Label>
                    <Input type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)} />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Min cart value (optional)</Label>
                  <Input
                    type="number"
                    value={minCartValue}
                    onChange={(e) => setMinCartValue(e.target.value === "" ? "" : Number(e.target.value))}
                    placeholder="e.g. 500"
                  />
                </div>

                <Separator />

                <TabsContent value="discount" className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Mode</Label>
                      <Select value={discountMode} onValueChange={(v) => setDiscountMode(v as any)}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select mode" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percent">Percentage</SelectItem>
                          <SelectItem value="flat">Fixed amount</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Value</Label>
                      <Input
                        type="number"
                        value={discountAmount}
                        onChange={(e) => setDiscountAmount(e.target.value === "" ? "" : Number(e.target.value))}
                        placeholder={discountMode === "percent" ? "10" : "50"}
                      />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="combo" className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Combo products</Label>
                    <div className="grid grid-cols-1 gap-2">
                      <ProductSearchDropdown
                        value={null}
                        onChange={(opt) => {
                          if (!opt) return;
                          setComboProducts((prev) => (prev.some((p) => p.id === opt.id) ? prev : [...prev, opt]));
                        }}
                        placeholder="Search and add product…"
                      />

                      {comboProducts.length ? (
                        <div className="flex flex-wrap gap-2">
                          {comboProducts.map((p) => (
                            <Button
                              key={p.id}
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-7"
                              onClick={() => setComboProducts((prev) => prev.filter((x) => x.id !== p.id))}
                              title="Remove"
                            >
                              {p.name}
                              {p.sku ? ` (${p.sku})` : ""}
                            </Button>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-muted-foreground">Add at least 2 products</div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Bundle price</Label>
                    <Input
                      type="number"
                      value={comboBundlePrice}
                      onChange={(e) => setComboBundlePrice(e.target.value === "" ? "" : Number(e.target.value))}
                      placeholder="999"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="bxgy" className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Buy product</Label>
                      <ProductSearchDropdown
                        value={buyProduct}
                        onChange={(opt) => setBuyProduct(opt)}
                        placeholder="Search product…"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Buy qty</Label>
                      <Input type="number" value={buyQty} onChange={(e) => setBuyQty(e.target.value === "" ? "" : Number(e.target.value))} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Get product</Label>
                      <ProductSearchDropdown
                        value={getProduct}
                        onChange={(opt) => setGetProduct(opt)}
                        placeholder="Search product…"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Get qty (free)</Label>
                      <Input type="number" value={getQty} onChange={(e) => setGetQty(e.target.value === "" ? "" : Number(e.target.value))} />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="promocode" className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Promo code</Label>
                    <Input value={promoCode} onChange={(e) => setPromoCode(e.target.value)} placeholder="SAVE10" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Mode</Label>
                      <Select value={discountMode} onValueChange={(v) => setDiscountMode(v as any)}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select mode" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percent">Percentage</SelectItem>
                          <SelectItem value="flat">Fixed amount</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Value</Label>
                      <Input
                        type="number"
                        value={discountAmount}
                        onChange={(e) => setDiscountAmount(e.target.value === "" ? "" : Number(e.target.value))}
                        placeholder={discountMode === "percent" ? "10" : "50"}
                      />
                    </div>
                  </div>
                </TabsContent>
              </div>
            </Tabs>

            <Button className="w-full" onClick={() => void onCreate()} disabled={loading}>
              {loading ? "Saving…" : "Create offer"}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Existing offers</CardTitle>
            <Button variant="outline" onClick={() => void load()} disabled={loading}>
              Refresh
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading && rows.length === 0 ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="text-sm text-muted-foreground">No promotions yet</div>
            ) : (
              <div className="space-y-2">
                <Accordion type="single" collapsible className="rounded-lg border">
                  {rows.map((p) => (
                    <AccordionItem key={p.id} value={p.id} className="px-3">
                      <AccordionTrigger className="no-underline hover:no-underline">
                        <div className="w-full flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate">{p.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {p.type}
                              {p.code ? ` • code: ${p.code}` : ""}
                              {p.start_at ? ` • starts ${new Date(p.start_at).toLocaleString()}` : ""}
                              {p.end_at ? ` • ends ${new Date(p.end_at).toLocaleString()}` : ""}
                            </div>
                          </div>
                          <div className="shrink-0 flex items-center gap-2">
                            <Badge variant={p.status === "active" ? "default" : "secondary"}>{p.status}</Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!token) return;
                                const next = p.status === "active" ? "inactive" : "active";
                                void api.promotions
                                  .toggle({ token, id: p.id, status: next })
                                  .then(() => {
                                    toast.success(`Set ${next}`);
                                    void load();
                                  })
                                  .catch((err) => toast.error(err instanceof Error ? err.message : "Failed to toggle"));
                              }}
                            >
                              Toggle
                            </Button>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-3">
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div>
                            <div className="text-muted-foreground">Priority</div>
                            <div className="font-medium">{p.priority}</div>
                          </div>
                          <div>
                            <div className="text-muted-foreground">Usage</div>
                            <div className="font-medium">
                              {p.usage ? `${p.usage.used_count}/${p.usage.usage_limit ?? "∞"}` : "—"}
                            </div>
                          </div>
                        </div>

                        {p.product_ids?.length ? (
                          <div className="space-y-1">
                            <div className="text-xs text-muted-foreground">Products</div>
                            <div className="flex flex-wrap gap-2">
                              {p.product_ids.map((pid) => {
                                const meta = productNameById[String(pid)];
                                const label = meta ? `${meta.name}${meta.sku ? ` (${meta.sku})` : ""}` : `#${pid}`;
                                return (
                                  <Badge key={pid} variant="secondary" className="truncate">
                                    {label}
                                  </Badge>
                                );
                              })}
                            </div>
                          </div>
                        ) : null}

                        {p.rules?.length ? (
                          <div className="space-y-1">
                            <div className="text-xs text-muted-foreground">Rules</div>
                            <div className="space-y-1">
                              {p.rules.map((r) => (
                                <div key={r.id} className="rounded-md border px-2 py-1 text-xs">
                                  <span className="font-medium">{r.rule_type}</span> <span className="text-muted-foreground">{r.operator}</span>{" "}
                                  <span className="text-muted-foreground">{JSON.stringify(r.value)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-xs text-muted-foreground">No rules (auto-applies when active)</div>
                        )}

                        {p.benefits?.length ? (
                          <div className="space-y-1">
                            <div className="text-xs text-muted-foreground">Benefits</div>
                            <div className="space-y-1">
                              {p.benefits.map((b) => (
                                <div key={b.id} className="rounded-md border px-2 py-1 text-xs">
                                  <span className="font-medium">{b.benefit_type}</span>{" "}
                                  <span className="text-muted-foreground">{JSON.stringify(b.value)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

